import React, { useEffect, useMemo, useState } from 'react';
import {
    Currency,
    DemeterChartSeriesModel,
    DemeterChartSeriesStyle,
    DemeterDataFrequency,
    DemeterPageElementModel,
    DemeterPageType,
    ExchangeType,
    GetDemeterChartSeriesDataResponse,
    LeadingIndicatorType,
    MarketIndicatorTransformationType,
    UnitOfMeasure,
} from '../../../../Generated/Raven-Demeter';
import circledPlus from '../../../Assets/Icons/circledPlus.svg';
import areaSvg from '../../../Assets/Icons/RadioButtonIcons/areaIcon.svg';
import columnSvg from '../../../Assets/Icons/RadioButtonIcons/columnIcon.svg';
import lineSvg from '../../../Assets/Icons/RadioButtonIcons/lineIcon.svg';
import { chartColors } from '../../../Components/Charts/ChartDefinitions';
import RadioButtonCardSeries from '../../../Components/Form/Buttons/RadioButtonCardSeries/RadioButtonCardSeries';
import ColorSelectorDropdown from '../../../Components/Form/Inputs/ColorSelectorDropdown';
import CurrencyDropdown from '../../../Components/Form/Inputs/CurrencyDropdown';
import Dropdown from '../../../Components/Form/Inputs/Dropdown';
import TextInput from '../../../Components/Form/Inputs/TextInput';
import UnitOfMeasureGeneralDropdown from '../../../Components/Form/Inputs/UnitOfMeasureGeneralDropdown';
import { IPageElementModalSelection } from '../../../Components/Modals/PageElementModal/PageElementModalDefinitions';
import PageElementModalWithoutChart from '../../../Components/Modals/PageElementModal/PageElementModalWithoutChart';
import useSearchParameters from '../../../Components/Navigation/Hooks/useSearchParametersHook';
import RegexValidators from '../../../Core/Validation/RegexValidators';
import useLanguage from '../../../Services/Language/useLanguageHook';
// eslint-disable-next-line max-len
import DataSelectorDataTransformationDropdown from '../../Administration/MarketIndicatorsManagement/MarketIndicatorFactor/MarketIndicatorsFactorFields/Common/DataSelectorDataTransformation';
// eslint-disable-next-line max-len
import DataSelectorTransformationValueTextInput from '../../Administration/MarketIndicatorsManagement/MarketIndicatorFactor/MarketIndicatorsFactorFields/Common/DataSelectorTransformationValueTextInput';
import { transformationTypesRequiringValues } from '../../Administration/MarketIndicatorsManagement/MarketIndicatorsManagementDefinitions';
import styles from '../ChartBuilder.module.scss';
import { ChartBuilderRequest, DataSourceSelection as DataSourceModel, defaultDataSource, leadingIndicatorTypeMap } from '../ChartBuilderDefinitions';

interface IChartBuilderDataSourceOptionsProps {
    chartBuilderUpsertRequest: ChartBuilderRequest;
    setChartBuilderRequest: (chartBuilderRequest: ChartBuilderRequest) => void;
    pageElements: DemeterPageElementModel[];
    dataSourceIndex?: number;
    isCalculatedSeries?: boolean;
    listDemeterChartDataResponse?: GetDemeterChartSeriesDataResponse[];
    colorsInUse: string[];
}

const defaultDataFrequency = DemeterDataFrequency.Monthly;

const notAllowedPageTypes: DemeterPageType[] = [DemeterPageType.MonitorsAndIndicators, DemeterPageType.NewsAndSummaries, DemeterPageType.BalanceTable];

const ChartBuilderDataSourceOptions: React.FC<IChartBuilderDataSourceOptionsProps> = (props: IChartBuilderDataSourceOptionsProps) => {
    // Application hooks.
    const [translations, translate] = useLanguage();
    const [searchParameters] = useSearchParameters();

    // Display hooks.
    const dataSeriesIndex = +(searchParameters.dataSeriesTab?.replace('dataSeries', '') || 0);
    const [isDataSeriesModalOpen, setIsDataSeriesModalOpen] = useState(false);
    const [availablePageElements, setAvailablePageElements] = useState(props.pageElements);

    // Options.
    const radioButtonOptions = useMemo(
        () => [
            {
                label: (
                    <>
                        {translations.physicalPrices.chartTypes.lineChart}
                        <img src={lineSvg} alt="" />
                    </>
                ),
                value: DemeterChartSeriesStyle.Line,
            },
            {
                label: (
                    <>
                        {translations.physicalPrices.chartTypes.areaChart}
                        <img src={areaSvg} alt="" />
                    </>
                ),
                value: DemeterChartSeriesStyle.Area,
            },
            {
                label: (
                    <>
                        {translations.physicalPrices.chartTypes.column}
                        <img src={columnSvg} alt="" />
                    </>
                ),
                value: DemeterChartSeriesStyle.Bar,
            },
        ],
        [translations],
    );

    const yAxisOptions = useMemo(() => {
        const options: { label: string; value: number | undefined }[] = [
            {
                label: translations.chartBuilder.text.y1,
                value: 0,
            },
            {
                label: translations.chartBuilder.text.y2,
                value: 1,
            },
        ];

        if (!props.isCalculatedSeries) {
            options.push({
                label: translations.chartBuilder.text.doNotChart,
                value: undefined,
            });
        }

        return options;
    }, [translations]);

    // Helpers.
    const currentDataSeries = [...props.chartBuilderUpsertRequest.rows];
    const chartBuilderDataSeries = props.chartBuilderUpsertRequest?.rows[dataSeriesIndex];
    const selection = chartBuilderDataSeries?.sources!.find((y, index) => index === props.dataSourceIndex)! as DataSourceModel;
    const showTransformationValueTextInput = selection?.transformationType && transformationTypesRequiringValues.includes(selection.transformationType);
    const selectionIsFuturesOrPrices =
        selection?.leadingIndicatorType === LeadingIndicatorType.CommodityFuturesPrice ||
        selection?.leadingIndicatorType === LeadingIndicatorType.CommodityMonthlyPrices;

    const selectionOrCalculatedRow = props.isCalculatedSeries
        ? chartBuilderDataSeries
        : (chartBuilderDataSeries?.sources!.find((y, index) => index === props.dataSourceIndex)! as DataSourceModel);

    const dataSourceHasNoRows = useMemo(() => {
        if (!props.listDemeterChartDataResponse || !selectionOrCalculatedRow || props.isCalculatedSeries) {
            return false;
        }

        const currentSourceHasNoRows =
            !props.isCalculatedSeries && props.listDemeterChartDataResponse[dataSeriesIndex]?.seriesData?.sourceData[props.dataSourceIndex!]?.rows.length === 0;

        return currentSourceHasNoRows;
    }, [props.isCalculatedSeries, props.listDemeterChartDataResponse]);

    useEffect(() => {
        if (selection?.unitOfMeasure) {
            return;
        }

        handleSetCurrency(undefined);
    }, [selection?.unitOfMeasure]);

    const handleUpdateDataSource = (overwriteValue?: DataSourceModel) => {
        props.setChartBuilderRequest({
            ...props.chartBuilderUpsertRequest,
            rows: currentDataSeries?.map((x, index) => {
                if (index === dataSeriesIndex) {
                    return {
                        ...x,
                        dataFrequency: overwriteValue?.dataFrequency ?? defaultDataFrequency,
                        sources: x.sources!.map((y, currentDataSourceIndex) => {
                            const isChangeIndex = props.dataSourceIndex === currentDataSourceIndex;

                            if (!overwriteValue && isChangeIndex) {
                                return defaultDataSource;
                            }

                            if (isChangeIndex) {
                                return { ...y, ...overwriteValue };
                            }

                            return y;
                        }),
                    };
                }
                return x;
            }),
        });
    };

    const handleUpdatCalculatedSeries = (overwriteValue?: DemeterChartSeriesModel) => {
        props.setChartBuilderRequest({
            ...props.chartBuilderUpsertRequest,
            rows: props.chartBuilderUpsertRequest.rows.map((x, index) => {
                if (index === dataSeriesIndex) {
                    return { ...x, ...overwriteValue };
                }

                return x;
            }),
        });
    };

    // For updating data sources only.
    const handleAddDataSeriesSelection = (selectedElement: IPageElementModalSelection) => {
        const dataFrequency = selectedElement.pageType === DemeterPageType.Futures ? DemeterDataFrequency.Daily : defaultDataFrequency;
        let { extraParameters } = selectedElement;
        if (selectedElement.pageType === DemeterPageType.Futures) {
            extraParameters = `${selectedElement.contractNumber}`;
        }

        if (selectedElement.pageType === DemeterPageType.PhysicalPrices) {
            extraParameters = selectedElement.dataSource;
        }

        const selectedElementIsFuturesOrPrices =
            selectedElement.pageType === DemeterPageType.Futures || selectedElement.pageType === DemeterPageType.PhysicalPrices;

        // OTC sources share the Futures page type but have no exchange-traded futures symbol, so
        // they must be routed to the OTC prices data path instead of CommodityFuturesPrice.
        const leadingIndicatorType =
            selectedElement.exchange === ExchangeType.Otc
                ? LeadingIndicatorType.CommodityOtcPrices
                : leadingIndicatorTypeMap.find((x) => x.pageType === selectedElement.pageType)?.leadingIndicatorType;

        let currentUnitOfMeasure = selection.unitOfMeasure;
        if (selection?.market !== selectedElement?.market) {
            currentUnitOfMeasure = undefined;
        }

        handleUpdateDataSource({
            ...selectedElement,
            commodity: selectedElement.subCommodity ?? selectedElement.commodity,
            dataFrequency: selectedElement?.dataFrequency ?? dataFrequency,
            leadingIndicatorType,
            extraParameters,
            unitOfMeasure: currentUnitOfMeasure,
            currency: selectedElementIsFuturesOrPrices ? selection.currency : undefined,
        } as DataSourceModel);
    };
    const handleSetCurrency = (currency?: Currency) => handleUpdateDataSource({ currency } as DataSourceModel);
    const handleSetUnitOfMeasure = (unitOfMeasure?: UnitOfMeasure) => handleUpdateDataSource({ unitOfMeasure } as DataSourceModel);
    const handleSetTransformationType = (transformationType?: MarketIndicatorTransformationType) =>
        handleUpdateDataSource({ transformationType } as DataSourceModel);
    const handleUpdateDataTransformationValue = (transformationValue?: number) => handleUpdateDataSource({ transformationValue } as DataSourceModel);
    const handleIncludeInChartChange = (includeInChart: boolean) => handleUpdateDataSource({ includeInChart } as DataSourceModel);

    // For updating common fields.
    const handleAxisChange = (axis?: number) => {
        if (!axis && axis !== 0) {
            handleIncludeInChartChange(!!axis);
            return;
        }

        if (props.isCalculatedSeries) {
            handleUpdatCalculatedSeries({ axis, includeInChart: true } as DemeterChartSeriesModel);
            return;
        }

        handleUpdateDataSource({ axis, includeInChart: true } as DataSourceModel);
    };
    const handleStyleChange = (style?: DemeterChartSeriesStyle) => {
        if (props.isCalculatedSeries) {
            handleUpdatCalculatedSeries({ style } as DemeterChartSeriesModel);
            return;
        }

        handleUpdateDataSource({ style } as DataSourceModel);
    };
    const handleColorChange = (color: string) => {
        if (props.isCalculatedSeries) {
            handleUpdatCalculatedSeries({ color } as DemeterChartSeriesModel);
            return;
        }

        handleUpdateDataSource({ color } as DataSourceModel);
    };
    const handleResetDataSeriesSelection = () => handleUpdateDataSource();

    const selectionBreadCrumbs = useMemo(() => {
        if (!selection?.pageType && !selection?.leadingIndicatorType) {
            return (
                <div className={styles.chart_builder_choose_data_series_button_content}>
                    <img src={circledPlus} alt="#" />
                    {translations.chartBuilder.text.chooseDataSource}
                </div>
            );
        }
        const isValid = (string?: string | null) => string && string !== null;

        const pageTypeString = selection?.pageType ?? leadingIndicatorTypeMap.find((x) => x.leadingIndicatorType === selection.leadingIndicatorType)?.pageType;

        const commodityString = isValid(selection.commodityDisplayName)
            ? translate(selection.commodityDisplayName ?? '')
            : translations.commodity[selection?.commodity!];

        const subCommodityString = isValid(selection.subCommodityDisplayName)
            ? translate(selection.subCommodityDisplayName ?? '')
            : translate(selection.subCommodity ?? '');

        const regionString = isValid(selection.regionDisplayName)
            ? translate(selection.regionDisplayName ?? '')
            : translations.region[selection?.region!] || translations.exchange[selection?.exchange!];

        const subRegionString = isValid(selection.subRegionDisplayName)
            ? translate(selection.subRegionDisplayName ?? '')
            : translate(selection.subRegion ?? '');

        let breadCrumbs = `${translations.pageType[pageTypeString!]} / ${commodityString}`;

        if (subCommodityString) {
            breadCrumbs = breadCrumbs.concat(` / ${subCommodityString}`);
        }

        if (regionString) {
            breadCrumbs = breadCrumbs.concat(` / ${regionString}`);
        }

        if (subRegionString) {
            breadCrumbs = breadCrumbs.concat(` / ${subRegionString}`);
        }

        const displayDataFrequency = selection.leadingIndicatorType === LeadingIndicatorType.CommodityMonthlyPrices;

        if (selection?.dataFrequency && displayDataFrequency) {
            breadCrumbs = breadCrumbs.concat(` / ${selection.dataFrequency}`);
        }

        const displayContractNumber =
            selection.leadingIndicatorType === LeadingIndicatorType.CommodityFuturesPrice ||
            selection.leadingIndicatorType === LeadingIndicatorType.CommodityOtcPrices;
        const futuresParameters = selection?.contractNumber || selection.extraParameters;

        if (futuresParameters && displayContractNumber) {
            breadCrumbs = breadCrumbs.concat(` / ${translations.words.contract} ${futuresParameters}`);
        }

        return breadCrumbs;
    }, [translations, props.chartBuilderUpsertRequest, selection]);

    const notAllowedTransformationTypes = useMemo(() => {
        if (selection?.dataFrequency === DemeterDataFrequency.Daily && selection.leadingIndicatorType === LeadingIndicatorType.CommodityFuturesPrice) {
            return undefined;
        }

        const notAllowed: MarketIndicatorTransformationType[] = [MarketIndicatorTransformationType.RollingAverageForwardContracts];

        if (selection?.dataFrequency === DemeterDataFrequency.Daily) {
            return notAllowed;
        }

        notAllowed.push(MarketIndicatorTransformationType.DayOverDayChange);

        if (selection?.dataFrequency === DemeterDataFrequency.Weekly) {
            return notAllowed;
        }

        notAllowed.push(MarketIndicatorTransformationType.WeekOverWeekChange);
        return notAllowed;
    }, [props.chartBuilderUpsertRequest]);

    const colorOptions = useMemo(() => {
        if (selectionOrCalculatedRow?.style === DemeterChartSeriesStyle.Line) {
            return chartColors.lineChartColorsRuleSetOne;
        }

        return chartColors.barChartColorsRuleSetTwo;
    }, [props.chartBuilderUpsertRequest]);

    useEffect(() => {
        if (colorOptions.includes(selectionOrCalculatedRow?.color!)) {
            return;
        }

        const currentColor = colorOptions.find((x) => !props.colorsInUse.includes(x));
        handleColorChange(currentColor!);
    }, [colorOptions]);

    useEffect(() => {
        const numberOfSelections = props.chartBuilderUpsertRequest.rows[dataSeriesIndex].sources.filter((x) => x.market).length;
        const availablePageElementsNotSet = !availablePageElements.length;
        const numberOfSelectionsOneOrLess = numberOfSelections <= 1;
        const noSelectionsChosen = numberOfSelections === 0;

        if (availablePageElementsNotSet || noSelectionsChosen || (numberOfSelectionsOneOrLess && selection?.market)) {
            setAvailablePageElements(props.pageElements);
            return;
        }

        const availableMarket = props.chartBuilderUpsertRequest.rows[dataSeriesIndex].sources.find((x) => x.market)?.market;
        setAvailablePageElements(props.pageElements.filter((x) => x.market === availableMarket));
    }, [selection, props.pageElements, dataSeriesIndex]);

    return (
        <>
            {dataSourceHasNoRows && (
                <div className={styles.chart_builder_no_rows}>
                    <p className={styles.chart_builder_no_rows_text}>{translations.chartBuilder.text.noRows}</p>
                </div>
            )}
            {props.isCalculatedSeries && (
                <div className={styles.chart_builder_line_break}>
                    <h3>{translations.chartBuilder.headers.calculatedSeries}</h3>
                </div>
            )}

            {!props.isCalculatedSeries && (
                <>
                    <div className={styles.chart_builder_selection_row}>
                        <button className={styles.chart_builder_choose_data_series_button} type="button" onClick={() => setIsDataSeriesModalOpen(true)}>
                            <p>{selectionBreadCrumbs}</p>
                        </button>
                        {selection?.commodity && (
                            <button className={styles.chart_builder_clear_button} type="button" onClick={handleResetDataSeriesSelection}>
                                <p>{selection && translations.words.clear}</p>
                            </button>
                        )}
                    </div>

                    <PageElementModalWithoutChart
                        title={translations.words.choose}
                        open={isDataSeriesModalOpen}
                        pageElements={availablePageElements}
                        selectedPageElement={selection}
                        handleAdd={handleAddDataSeriesSelection}
                        handleClose={() => setIsDataSeriesModalOpen(false)}
                        resetOnOpen={!selection.commodity}
                        track={{ eventCategory: 'ChartBuilder', eventAction: 'ModalChooseDataSource' }}
                    />
                </>
            )}

            <div className={styles.chart_builder_row}>
                <TextInput
                    title={translations.chartBuilder.fields.appearInLegendAs}
                    value={selectionOrCalculatedRow.name}
                    handleTextChange={(name) => {
                        if (props.isCalculatedSeries) {
                            handleUpdatCalculatedSeries({ name } as DemeterChartSeriesModel);
                            return;
                        }

                        handleUpdateDataSource({ name } as DataSourceModel);
                    }}
                    validation={RegexValidators.AlphaNumericMinimumLength2}
                    track={{ eventCategory: 'ChartBuilder', eventAction: 'AppearInLegendAsChanged', eventValue: selectionOrCalculatedRow.name }}
                />
            </div>

            {!props.isCalculatedSeries && (
                <>
                    <div className={styles.chart_builder_row}>
                        <div className={styles.chart_builder_dropdown}>
                            <UnitOfMeasureGeneralDropdown
                                disabled={!selection}
                                label={translations.words.unit}
                                market={selection?.market}
                                unitOfMeasure={selection?.unitOfMeasure}
                                handleChange={handleSetUnitOfMeasure}
                                track={{ eventCategory: 'ChartBuilder', eventAction: 'UnitOfMeasureChanged', eventValue: selection?.unitOfMeasure }}
                            />
                        </div>
                        {/* For the backend, we cannot select a currency with default unit of measure. May
                        want to change this in the future. */}
                        {selectionIsFuturesOrPrices && selection.unitOfMeasure && (
                            <div className={styles.chart_builder_dropdown}>
                                <CurrencyDropdown
                                    disabled={!selection}
                                    label={translations.words.currency}
                                    currency={selection?.currency}
                                    handleChange={handleSetCurrency}
                                    track={{ eventCategory: 'ChartBuilder', eventAction: 'CurrencyChanged', eventValue: selection?.currency }}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.chart_builder_row}>
                        <div className={styles.chart_builder_dropdown}>
                            <DataSelectorDataTransformationDropdown
                                disabled={!selection}
                                transformationType={selection?.transformationType}
                                handleChange={handleSetTransformationType}
                                track={{ eventCategory: 'ChartBuilder', eventAction: 'TransformationTypeChanged', eventValue: selection?.transformationType }}
                                notAllowedTransformationTypes={notAllowedTransformationTypes}
                            />
                        </div>
                    </div>
                    {showTransformationValueTextInput && (
                        <div className={styles.chart_builder_row}>
                            <div className={styles.chart_builder_transformation_value}>
                                <DataSelectorTransformationValueTextInput
                                    transformationValue={selection?.transformationValue!}
                                    handleChange={handleUpdateDataTransformationValue}
                                    transformationType={selection?.transformationType}
                                    track={{
                                        eventCategory: 'ChartBuilder',
                                        eventAction: 'TransformationValueChanged',
                                        eventValue: selection?.transformationValue,
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
            <div className={styles.chart_builder_row}>
                <div className={styles.chart_builder_dropdown}>
                    <Dropdown
                        label={translations.chartBuilder.fields.axis}
                        options={yAxisOptions}
                        value={selectionOrCalculatedRow.includeInChart ? selectionOrCalculatedRow?.axis ?? 0 : undefined}
                        handleOptionChange={handleAxisChange}
                        track={{
                            eventCategory: 'ChartBuilder',
                            eventAction: 'AxisChanged',
                            eventValue: selectionOrCalculatedRow.includeInChart ? selectionOrCalculatedRow?.axis ?? 0 : 'NotInChart',
                        }}
                    />
                </div>
            </div>
            <RadioButtonCardSeries
                label={translations.chartBuilder.fields.chartType}
                options={radioButtonOptions}
                handleSelectionChange={handleStyleChange}
                selection={selectionOrCalculatedRow?.style}
                track={{ eventCategory: 'ChartBuilder', eventAction: 'ChartTypeChanged', eventValue: selectionOrCalculatedRow?.style }}
            />
            <div className={styles.chart_builder_row}>
                <div className={styles.chart_builder_color_selection_row}>
                    <ColorSelectorDropdown
                        label={translations.chartBuilder.fields.lineColor}
                        value={selectionOrCalculatedRow?.color!}
                        options={colorOptions.filter((x) => !(props.colorsInUse.includes(x) && x !== selectionOrCalculatedRow?.color))}
                        handleOptionChange={handleColorChange}
                        testId="ChartBuilderColorSelectorDropdown"
                    />
                </div>
            </div>
        </>
    );
};

export default ChartBuilderDataSourceOptions;
